"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProductShowcase() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "Surgical Devices",
    description: "Precision-engineered surgical instruments and systems sourced from global leaders to enhance clinical outcomes in the operating room.",
    Icon: LineChart
  }, {
    number: "02",
    title: "Diagnostic Devices",
    description: "Advanced diagnostic imaging and testing equipment that provides healthcare professionals with accurate, real-time clinical data.",
    Icon: Brain
  }, {
    number: "03",
    title: "Medical Aesthetics",
    description: "Innovative dermatological and aesthetic solutions designed to meet the growing global demand for non-invasive skin care treatments.",
    Icon: Radar
  }, {
    number: "04",
    title: "Dental Solutions",
    description: "Comprehensive dental technology and restorative materials that support modern practices in delivering superior patient care.",
    Icon: Compass
  }, {
    number: "05",
    title: "AI Healthcare",
    description: "Cutting-edge artificial intelligence platforms that streamline clinical workflows and improve diagnostic accuracy through machine learning.",
    Icon: Activity
  }, {
    number: "06",
    title: "Digital Healthcare",
    description: "Integrated digital health ecosystems and remote monitoring tools that connect patients and providers for seamless care delivery.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="productshowcase" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-default text-xs tracking-widest uppercase text-teal-600 mb-6">
              03 / Our Portfolio
            </p>
            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900">
              Premium Medical Technologies.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl">
              We bridge the gap between innovative global manufacturers and healthcare providers. Our curated portfolio represents the gold standard in medical technology, carefully evaluated for clinical efficacy and regulatory compliance.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-white border border-transparent hover:border-teal-500/40 transition-all duration-500 ease-out p-8 sm:p-10 flex flex-col rounded-lg shadow-sm" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                {}
                <span className="font-default text-xs tracking-widest text-teal-600">
                  {cap.number}
                </span>

                {}
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-teal-50 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1} className="relative size-8 text-slate-900 group-hover:text-teal-600 transition-colors duration-500" />
                </div>

                {}
                <h3 className="font-default font-semibold text-2xl leading-snug text-slate-900 mb-4">
                  {cap.title}
                </h3>

                {}
                <p className="font-default text-[15px] leading-relaxed text-slate-600 mb-8">
                  {cap.description}
                </p>

                {}
                <div className="mt-auto">
                  <Link to="/OurProductsPage" className="inline-flex items-center gap-2 font-default text-xs tracking-widest uppercase text-slate-900 hover:text-teal-600 transition-colors duration-300">
                    <span className="relative">
                      <span className="bg-left-bottom bg-gradient-to-r from-teal-600 to-teal-600 bg-[length:0%_1px] bg-no-repeat group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out pb-0.5">
                        View Details
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-slate-200">
          <p className="font-default text-sm text-slate-600 max-w-xl">
            Partner with MS Biotechnology to access a world-class network of medical innovation.
          </p>
          <Button asChild size="lg" className="bg-slate-900 text-white hover:bg-slate-800 font-default text-xs tracking-widest uppercase rounded-lg">
            <Link to="/OurProductsPage">Explore Full Catalog</Link>
          </Button>
        </div>
      </div>
    </section>;
}